import draw1 from './sfx/card-draw-1.mp3';
import draw2 from './sfx/card-draw-2.mp3';
import draw3 from './sfx/card-draw-3.mp3';
import fan from './sfx/card-fan.mp3';
import place1 from './sfx/card-place-1.mp3';
import place2 from './sfx/card-place-2.mp3';
import place3 from './sfx/card-place-3.mp3';
import shuffle from './sfx/card-shuffle.mp3';

/**
 * Sound effects (Appendix A): tactile feedback for moves and scores, a sound per pattern and a
 * distinctive chime for Full Circle, synthesized; and the cards themselves, recorded: drawing,
 * laying on the pile, dealing and shuffling (Kenney's Casino Audio, CC0, see CREDITS.md).
 */

/** Recorded card sounds, with variants picked at random so repeats don't sound mechanical. */
const SAMPLES = {
  draw: [draw1, draw2, draw3],
  place: [place1, place2, place3],
  fan: [fan],
  shuffle: [shuffle],
} as const;
type SampleName = keyof typeof SAMPLES;

export type SoundName =
  | SampleName
  | 'card'
  | 'move'
  | 'capture'
  | 'crown'
  | 'ladder'
  | 'echo'
  | 'streak'
  | 'fullCircle'
  | 'tick'
  | 'score'
  | 'pass'
  | 'epitaph'
  | 'pawned'
  | 'win'
  | 'lose'
  // The Long Night's objects (docs/LONG_NIGHT_ART_DIRECTION.md §10.3), in one dark scale.
  | 'slam'
  | 'slamBig'
  | 'flare'
  | 'record'
  | 'tear'
  | 'paper'
  | 'wax'
  | 'snip'
  | 'glass'
  | 'curtain'
  | 'match'
  | 'snuff'
  | 'nib'
  | 'breath'
  | 'thud'
  | 'stamp'
  | 'creak'
  | 'ember'
  | 'scratch'
  | 'drip'
  | 'flit'
  | 'lowBell'
  | 'clockTick'
  | 'enso'
  | 'thunder'
  | 'tap';

/**
 * The Long Night's scale (§10.2): the Japanese in scale on D (D, E♭, G, A, B♭), calm and a little
 * uneasy at once, from D3 up. The Tally climbs it one step a beat, D4 to D6, and holds there.
 */
const IN_SCALE = [146.83, 155.56, 196.0, 220.0, 233.08];
export const inNote = (step: number): number =>
  (IN_SCALE[((step % 5) + 5) % 5] as number) * 2 ** Math.floor(step / 5);
/** D4, the Tally's first beat, as a step of the scale above D3. */
const LADDER_START = 5;
/** D6: the ladder holds there. */
const LADDER_TOP = 15;

type Ctor = typeof AudioContext;

export class SoundBoard {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private buffers = new Map<string, AudioBuffer | 'loading' | 'failed'>();
  private on = true;
  volume = 0.6;

  get enabled(): boolean {
    return this.on;
  }

  /** Sound off silences the room tone too; on again, it comes back. */
  set enabled(value: boolean) {
    if (value === this.on) return;
    this.on = value;
    const want = this.wanted;
    this.setAmbience(null);
    this.wanted = want;
    if (value) this.setAmbience(want);
  }

  /** Plays a recorded card sound, loading it the first time (the synthesized one meanwhile). */
  private sample(name: SampleName): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const urls = SAMPLES[name];
    const url = urls[Math.floor(Math.random() * urls.length)] as string;
    const buffer = this.buffers.get(url);
    if (buffer && buffer !== 'loading' && buffer !== 'failed') {
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      const g = ctx.createGain();
      g.gain.value = name === 'shuffle' ? 0.8 : 1.1;
      src.connect(g).connect(this.master);
      src.start();
      return;
    }
    if (buffer !== 'failed') this.thump(0, 0.09, 0.5, 1800);
    if (buffer) return;
    // Load every variant of this sound for next time.
    for (const u of urls) {
      if (this.buffers.has(u)) continue;
      this.buffers.set(u, 'loading');
      void fetch(u)
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
        .then((data) => ctx.decodeAudioData(data))
        .then(
          (decoded) => this.buffers.set(u, decoded),
          () => this.buffers.set(u, 'failed'),
        );
    }
  }

  private ensure(): AudioContext | null {
    if (!this.enabled) return null;
    if (!this.ctx) {
      const w = globalThis as unknown as { AudioContext?: Ctor; webkitAudioContext?: Ctor };
      const AC = w.AudioContext ?? w.webkitAudioContext;
      if (!AC) return null;
      try {
        this.ctx = new AC();
        this.master = this.ctx.createGain();
        this.master.connect(this.ctx.destination);
      } catch {
        this.enabled = false;
        return null;
      }
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume().catch(() => undefined);
    if (this.master) this.master.gain.value = this.volume * 0.5;
    return this.ctx;
  }

  private tone(
    freq: number,
    start: number,
    dur: number,
    type: OscillatorType = 'sine',
    gain = 0.3,
    glideTo?: number,
  ): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t0 = ctx.currentTime + start;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    if (glideTo) osc.frequency.exponentialRampToValueAtTime(glideTo, t0 + dur);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g).connect(this.master);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  }

  private thump(start: number, dur: number, gain: number, lowpass: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t0 = ctx.currentTime + start;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer(ctx);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = lowpass;
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, t0);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(filter).connect(g).connect(this.master);
    src.start(t0);
    src.stop(t0 + dur + 0.02);
  }

  private noiseBuffer(ctx: AudioContext): AudioBuffer {
    if (!this.noise) {
      this.noise = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.3), ctx.sampleRate);
      const data = this.noise.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    }
    return this.noise;
  }

  /** Filtered noise: paper, wax, smoke, breath. The filter may sweep (`to`). */
  private hiss(
    start: number,
    dur: number,
    gain: number,
    type: BiquadFilterType,
    freq: number,
    to?: number,
    attack = 0.005,
    q = 0.8,
  ): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t0 = ctx.currentTime + start;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer(ctx);
    src.loop = dur > 0.28;
    const filter = ctx.createBiquadFilter();
    filter.type = type;
    filter.Q.value = q;
    filter.frequency.setValueAtTime(freq, t0);
    if (to) filter.frequency.exponentialRampToValueAtTime(to, t0 + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(filter).connect(g).connect(this.master);
    src.start(t0);
    src.stop(t0 + dur + 0.02);
  }

  /** A bell: a fundamental and inharmonic partials, with a long tail. */
  private bellTone(freq: number, start: number, tail: number, gain: number): void {
    [
      [1, 1],
      [2.01, 0.45],
      [2.76, 0.3],
      [5.4, 0.12],
    ].forEach(([ratio, level]) =>
      this.tone(freq * ratio!, start, tail / ratio!, 'sine', gain * level!),
    );
  }

  /**
   * One beat of the Tally (§10.2): each beat a step higher on the ladder, D4 to D6, where it
   * holds. A ×M sounds a step lower, doubled an octave down, with a heavier thud.
   */
  tallyBeat(beat: number, kind: 'p' | 'm' | 'x' | 'brass', ink = false): void {
    if (!this.ensure()) return;
    const step = Math.min(LADDER_TOP, LADDER_START + beat);
    if (ink) {
      // The Broker's count is written: a nib, and the note very quietly.
      this.hiss(0, 0.08, 0.05, 'bandpass', 4200, 2600, 0.004, 3);
      this.tone(inNote(step), 0, 0.25, 'sine', 0.025);
      return;
    }
    if (kind === 'x') {
      const f = inNote(step - 1);
      this.tone(f, 0, 0.32, 'triangle', 0.12);
      this.tone(f / 2, 0, 0.4, 'sine', 0.12);
      this.thump(0, 0.14, 0.7, 700);
      return;
    }
    if (kind === 'brass') {
      this.clink(beat);
      return;
    }
    this.tone(inNote(step), 0, 0.18, 'triangle', kind === 'm' ? 0.1 : 0.09);
    this.tone(inNote(step) * 2, 0.005, 0.08, 'sine', 0.025);
  }

  /** A brass token: a short metallic clink, a step higher each time (`n`). */
  clink(n = 0): void {
    if (!this.ensure()) return;
    const f = inNote(LADDER_START + 5 + Math.min(n, 8));
    this.tone(f * 2.4, 0, 0.09, 'triangle', 0.05);
    this.tone(f * 3.1, 0.004, 0.06, 'sine', 0.035);
    this.hiss(0, 0.03, 0.04, 'highpass', 5000);
  }

  /** The Counter's bell (§10.3): D5 with a 2.5 s tail; each further ring a step lower. */
  bell(ring = 0): void {
    if (!this.ensure()) return;
    this.bellTone(inNote(10 - Math.min(ring, 6)), 0, 2.5, 0.09);
  }

  private ambience: { stop: () => void; level: (k: number) => void } | null = null;
  private ambienceKind: 'room' | 'clock' | null = null;
  /** What the scene asks for, whether or not sound is on. */
  private wanted: 'room' | 'clock' | null = null;

  /**
   * Room tone (§10.1): low filtered noise at the edge of hearing that swells every ten seconds or
   * so; on the Night board, a clock ticking once a second under it. `null` stops it.
   */
  setAmbience(kind: 'room' | 'clock' | null): void {
    this.wanted = kind;
    if (kind === this.ambienceKind) return;
    this.ambience?.stop();
    this.ambience = null;
    this.ambienceKind = null;
    if (!kind || !this.ensure()) return;
    this.ambienceKind = kind;
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer(ctx);
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 260;
    const g = ctx.createGain();
    g.gain.value = 0;
    g.gain.setTargetAtTime(0.05, ctx.currentTime, 1.5);
    // The swell: a slow wave on the level.
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.1;
    const depth = ctx.createGain();
    depth.gain.value = 0.025;
    lfo.connect(depth).connect(g.gain);
    src.connect(filter).connect(g).connect(this.master);
    src.start();
    lfo.start();
    const ticker =
      kind === 'clock'
        ? setInterval(() => {
            if (!this.enabled) return;
            this.tone(2600, 0, 0.02, 'square', 0.006);
            this.thump(0, 0.02, 0.05, 2400);
          }, 1000)
        : undefined;
    this.ambience = {
      stop: () => {
        clearInterval(ticker);
        const t = ctx.currentTime;
        g.gain.cancelScheduledValues(t);
        g.gain.setTargetAtTime(0, t, 0.3);
        lfo.stop(t + 1.5);
        src.stop(t + 1.5);
      },
      level: (k) => g.gain.setTargetAtTime(0.05 * k, ctx.currentTime, 0.2),
    };
  }

  /** A hush (§9.1): the room tone drops away by 12 dB, then comes back. */
  hush(on: boolean): void {
    this.ambience?.level(on ? 0.25 : 1);
  }

  play(name: SoundName): void {
    if (!this.ensure()) return;
    switch (name) {
      case 'draw':
      case 'place':
      case 'fan':
      case 'shuffle':
        this.sample(name);
        break;
      case 'card':
        this.thump(0, 0.09, 0.5, 1800);
        break;
      case 'move':
        this.thump(0, 0.05, 0.4, 3200);
        this.tone(520, 0, 0.06, 'triangle', 0.08);
        break;
      case 'capture':
        this.thump(0, 0.12, 0.8, 1200);
        this.tone(180, 0, 0.18, 'square', 0.06, 90);
        break;
      case 'crown':
        [880, 1320, 1760].forEach((f, i) => this.tone(f, i * 0.02, 0.9, 'sine', 0.12 / (i + 1)));
        break;
      case 'ladder':
        [392, 494, 587].forEach((f, i) => this.tone(f, i * 0.09, 0.25, 'triangle', 0.14));
        break;
      case 'echo':
        this.tone(466, 0, 0.18, 'triangle', 0.14);
        this.tone(466, 0.16, 0.18, 'triangle', 0.08);
        this.tone(466, 0.32, 0.18, 'triangle', 0.04);
        break;
      case 'streak':
        this.tone(330, 0, 0.3, 'sawtooth', 0.05, 660);
        break;
      case 'fullCircle':
        [523, 659, 784, 1047, 784, 659].forEach((f, i) => this.tone(f, i * 0.07, 0.6, 'sine', 0.1));
        this.tone(2093, 0.42, 1.2, 'sine', 0.05);
        break;
      case 'tick':
        this.tone(1400, 0, 0.03, 'square', 0.03);
        break;
      case 'score':
        this.tone(659, 0, 0.12, 'triangle', 0.12);
        this.tone(988, 0.08, 0.2, 'triangle', 0.1);
        break;
      case 'pass':
        this.tone(220, 0, 0.3, 'sine', 0.1, 180);
        break;
      case 'epitaph':
        [220, 262, 311].forEach((f) => this.tone(f, 0, 1.1, 'sine', 0.07));
        break;
      case 'pawned':
        this.tone(300, 0, 0.25, 'triangle', 0.1, 200);
        this.thump(0.05, 0.08, 0.3, 900);
        break;
      case 'win':
        [523, 659, 784, 1047].forEach((f, i) => this.tone(f, i * 0.12, 0.5, 'triangle', 0.12));
        break;
      case 'lose':
        [392, 330, 262].forEach((f, i) => this.tone(f, i * 0.16, 0.5, 'sine', 0.1));
        break;
      // The slam lands on a low D (D3), with a soft bell above it (§10.2).
      case 'slam':
        this.tone(inNote(0), 0, 0.5, 'sine', 0.2);
        this.thump(0, 0.16, 0.8, 600);
        this.bellTone(inNote(10), 0.02, 1.2, 0.035);
        break;
      case 'slamBig':
        this.tone(inNote(0), 0, 0.7, 'sine', 0.24);
        this.tone(inNote(-5), 0, 0.8, 'sine', 0.16);
        this.thump(0, 0.22, 0.95, 500);
        this.bellTone(inNote(10), 0.02, 1.8, 0.05);
        this.bellTone(inNote(13), 0.06, 1.6, 0.03);
        break;
      // Candle flame catching: a rising breath of air and a warm chord in the scale.
      case 'flare':
        this.hiss(0, 0.9, 0.08, 'bandpass', 500, 2400, 0.25, 0.7);
        [5, 7, 8, 10].forEach((st, i) => this.tone(inNote(st), 0.05 + i * 0.05, 1.2, 'sine', 0.05));
        break;
      // The best chain of the run: the ladder's end, an octave higher.
      case 'record':
        [15, 17, 18, 20].forEach((st, i) => this.tone(inNote(st), i * 0.09, 0.7, 'sine', 0.06));
        break;
      case 'tear':
        this.hiss(0, 0.16, 0.16, 'bandpass', 2600, 900, 0.004, 1.2);
        this.hiss(0.05, 0.09, 0.08, 'highpass', 3500);
        break;
      case 'paper':
        this.hiss(0, 0.32, 0.05, 'bandpass', 1400, 2400, 0.08, 0.9);
        break;
      case 'wax':
        this.thump(0, 0.05, 0.5, 2600);
        this.hiss(0.03, 0.18, 0.06, 'bandpass', 3000, 1500, 0.004, 2);
        break;
      case 'snip':
        this.tone(3400, 0, 0.03, 'square', 0.02);
        this.hiss(0, 0.04, 0.08, 'highpass', 4500);
        break;
      case 'glass':
        [inNote(15) * 1.5, inNote(18) * 1.5].forEach((f, i) =>
          this.tone(f, i * 0.03, 1.6, 'sine', 0.03),
        );
        break;
      case 'curtain':
        this.hiss(0, 1.1, 0.09, 'lowpass', 300, 900, 0.4);
        this.thump(0.85, 0.18, 0.3, 300);
        break;
      case 'match':
        this.hiss(0, 0.12, 0.18, 'bandpass', 3800, 1800, 0.003, 1.5);
        this.hiss(0.1, 0.6, 0.05, 'lowpass', 900, 500, 0.08);
        break;
      case 'snuff':
        this.hiss(0, 0.7, 0.07, 'lowpass', 1200, 300, 0.05);
        break;
      // A Superstition (or a Carving, an Omen) firing in the count: a dry tap of a card on wood.
      case 'tap':
        this.hiss(0, 0.05, 0.11, 'bandpass', 2100, 1300, 0.002, 2.4);
        this.thump(0, 0.06, 0.22, 900);
        break;
      // A boss's lightning: a crack, then thunder rolling away low.
      case 'thunder':
        this.hiss(0, 0.18, 0.2, 'highpass', 2400, 900, 0.002, 1.2);
        this.thump(0.02, 0.3, 1.2, 160);
        this.hiss(0.05, 2.4, 0.16, 'lowpass', 260, 90, 0.2);
        this.tone(inNote(-10), 0.05, 1.6, 'sine', 0.1);
        break;
      case 'nib':
        this.hiss(0, 0.05, 0.03, 'bandpass', 4600, 3000, 0.004, 4);
        break;
      // The Broker: no voice, ever. A low breath under each of his lines.
      case 'breath':
        this.hiss(0, 1.2, 0.06, 'lowpass', 400, 700, 0.5);
        break;
      case 'thud':
        this.thump(0, 0.14, 0.6, 500);
        this.tone(inNote(0), 0, 0.25, 'sine', 0.08);
        break;
      case 'stamp':
        this.thump(0, 0.2, 1, 450);
        this.tone(inNote(-5), 0, 0.5, 'sine', 0.18);
        this.tone(inNote(10) * 2.4, 0.01, 0.4, 'triangle', 0.03);
        break;
      case 'creak':
        this.tone(140, 0, 0.35, 'sawtooth', 0.02, 110);
        this.clink(2);
        break;
      case 'ember':
        this.hiss(0, 0.6, 0.06, 'bandpass', 1800, 700, 0.05, 1.5);
        this.hiss(0.1, 0.8, 0.03, 'highpass', 5000, 3000, 0.2);
        break;
      case 'scratch':
        [0, 0.14, 0.3, 0.42, 0.6].forEach((t0) =>
          this.hiss(t0, 0.09, 0.05, 'bandpass', 3800, 2600, 0.01, 3),
        );
        break;
      // The regulars' accents (§12.11): Old Tallow's wax drop, the Magpie's click, the Widow's
      // low bell.
      case 'drip':
        this.tone(inNote(13), 0, 0.12, 'sine', 0.06, inNote(8));
        break;
      case 'flit':
        this.tone(inNote(18), 0, 0.03, 'square', 0.025);
        this.tone(inNote(20), 0.04, 0.03, 'square', 0.02);
        break;
      case 'lowBell':
        this.bellTone(inNote(3), 0, 2.2, 0.06);
        break;
      case 'clockTick':
        this.tone(2600, 0, 0.02, 'square', 0.006);
        break;
      // Full Circle's ensō: the brush, over the chime.
      case 'enso':
        this.hiss(0, 0.6, 0.05, 'bandpass', 1200, 2600, 0.05, 1.2);
        break;
    }
  }
}

export const sounds = new SoundBoard();
